import { createId, isoNow } from './crypto';
import { getStudyDatabase, type StudyDatabase } from './database';
import { normalizeStudyRecord } from './schema';
import {
  CURRENT_SCHEMA_VERSION,
  recordKey,
  type DatabaseDump,
  type OutboxEntry,
  type RestoreMode,
  type SettingRecord,
  type SnapshotRecord,
  type StoredAttachment,
  type StudyRecord,
  type StudyRecordType,
} from './types';

const DEVICE_SETTING_KEY = 'system:device-id';

export interface SaveRecordOptions {
  queueSync?: boolean;
  preserveRevision?: boolean;
}

export interface SaveAttachmentOptions {
  queueSync?: boolean;
  preserveRevision?: boolean;
}

export function nextRevision(previousRevision: number | undefined, incomingRevision: number | undefined, preserve: boolean): number {
  if (preserve) return Math.max(1, incomingRevision ?? 1);
  return previousRevision === undefined ? 1 : previousRevision + 1;
}

export function nextOutboxBaseRevision(
  pendingBaseRevision: number | undefined,
  previousRevision: number | undefined,
): number {
  return pendingBaseRevision ?? previousRevision ?? 0;
}

export class IndexedDbStudyRepository {
  constructor(private readonly db: StudyDatabase = getStudyDatabase()) {}

  async getDeviceId(): Promise<string> {
    const current = await this.getSetting<string>(DEVICE_SETTING_KEY);
    if (current) return current;
    const deviceId = createId('device');
    await this.setSetting(DEVICE_SETTING_KEY, deviceId);
    return deviceId;
  }

  async getRecord<T extends StudyRecord = StudyRecord>(type: StudyRecordType, id: string): Promise<T | undefined> {
    return this.db.records.get(recordKey(type, id)) as Promise<T | undefined>;
  }

  async getRecordByKey<T extends StudyRecord = StudyRecord>(key: string): Promise<T | undefined> {
    return this.db.records.get(key) as Promise<T | undefined>;
  }

  async listRecords<T extends StudyRecord = StudyRecord>(options: {
    type?: StudyRecordType;
    studyDate?: string;
    includeDeleted?: boolean;
  } = {}): Promise<T[]> {
    let records: StudyRecord[];
    if (options.type && options.studyDate) {
      records = await this.db.records.where('[type+studyDate]').equals([options.type, options.studyDate]).toArray();
    } else if (options.type) {
      records = await this.db.records.where('type').equals(options.type).toArray();
    } else if (options.studyDate) {
      records = await this.db.records.where('studyDate').equals(options.studyDate).toArray();
    } else {
      records = await this.db.records.toArray();
    }
    return records
      .filter((record) => options.includeDeleted || !record.deletedAt)
      .sort((left, right) => left.updatedAt.localeCompare(right.updatedAt)) as T[];
  }

  async saveRecord<T extends StudyRecord>(record: T, options: SaveRecordOptions = {}): Promise<T> {
    const [saved] = await this.saveRecords([record], options);
    if (!saved) throw new Error('保存记录失败');
    return saved;
  }

  async saveRecords<T extends StudyRecord>(records: T[], options: SaveRecordOptions = {}): Promise<T[]> {
    if (!records.length) return [];
    const deviceId = await this.getDeviceId();
    const normalizedRecords = records.map((record) => normalizeStudyRecord(record, deviceId) as T);
    const queueSync = options.queueSync ?? true;

    return this.db.transaction('rw', this.db.records, this.db.outbox, async () => {
      const saved: T[] = [];
      for (const normalized of normalizedRecords) {
        const previous = await this.db.records.get(normalized.key);
        const existingOutbox = queueSync
          ? await this.db.outbox.where('entityKey').equals(normalized.key).first()
          : undefined;
        const now = isoNow();
        const next = {
          ...normalized,
          key: recordKey(normalized.type, normalized.id),
          schemaVersion: CURRENT_SCHEMA_VERSION,
          createdAt: previous?.createdAt ?? normalized.createdAt ?? now,
          updatedAt: options.preserveRevision ? normalized.updatedAt : now,
          revision: nextRevision(previous?.revision, normalized.revision, options.preserveRevision ?? false),
        } as T;
        await this.db.records.put(next);
        if (queueSync) {
          await this.db.outbox.where('entityKey').equals(next.key).delete();
          await this.db.outbox.put(this.makeOutbox(
            next.deletedAt ? 'record-delete' : 'record-upsert',
            next.key,
            nextOutboxBaseRevision(existingOutbox?.baseRevision, previous?.revision),
            next,
          ));
        }
        saved.push(next);
      }
      return saved;
    });
  }

  async deleteRecord(type: StudyRecordType, id: string): Promise<boolean> {
    const existing = await this.getRecord(type, id);
    if (!existing || existing.deletedAt) return false;
    await this.saveRecord({ ...existing, deletedAt: isoNow() });
    return true;
  }

  async saveAttachment(attachment: StoredAttachment, options: SaveAttachmentOptions = {}): Promise<StoredAttachment> {
    const queueSync = options.queueSync ?? true;
    return this.db.transaction('rw', this.db.attachments, this.db.outbox, async () => {
      const previous = await this.db.attachments.get(attachment.id);
      const entityKey = `attachment:${attachment.id}`;
      const existingOutbox = queueSync
        ? await this.db.outbox.where('entityKey').equals(entityKey).first()
        : undefined;
      const next: StoredAttachment = {
        ...attachment,
        schemaVersion: CURRENT_SCHEMA_VERSION,
        createdAt: previous?.createdAt ?? attachment.createdAt,
        updatedAt: options.preserveRevision ? attachment.updatedAt : isoNow(),
        revision: nextRevision(previous?.revision, attachment.revision, options.preserveRevision ?? false),
      };
      await this.db.attachments.put(next);
      if (queueSync) {
        await this.db.outbox.where('entityKey').equals(entityKey).delete();
        const { blob: _blob, ...metadata } = next;
        await this.db.outbox.put(this.makeOutbox(
          next.deletedAt ? 'attachment-delete' : 'attachment-upsert',
          entityKey,
          nextOutboxBaseRevision(existingOutbox?.baseRevision, previous?.revision),
          metadata,
        ));
      }
      return next;
    });
  }

  async getAttachment(id: string): Promise<StoredAttachment | undefined> {
    return this.db.attachments.get(id);
  }

  async listAttachments(ownerKey?: string, includeDeleted = false): Promise<StoredAttachment[]> {
    const attachments = ownerKey
      ? await this.db.attachments.where('ownerKey').equals(ownerKey).toArray()
      : await this.db.attachments.toArray();
    return attachments.filter((attachment) => includeDeleted || !attachment.deletedAt);
  }

  async deleteAttachment(id: string): Promise<boolean> {
    const attachment = await this.db.attachments.get(id);
    if (!attachment || attachment.deletedAt) return false;
    await this.saveAttachment({ ...attachment, deletedAt: isoNow() });
    return true;
  }

  async getSetting<T>(key: string): Promise<T | undefined> {
    return (await this.db.settings.get(key))?.value as T | undefined;
  }

  async setSetting<T>(key: string, value: T): Promise<void> {
    await this.db.settings.put({ key, value, updatedAt: isoNow() });
  }

  async listSettings(): Promise<SettingRecord[]> {
    return this.db.settings.toArray();
  }

  async listOutbox(): Promise<OutboxEntry[]> {
    return this.db.outbox.orderBy('createdAt').toArray();
  }

  async removeOutbox(ids: string[]): Promise<void> {
    if (!ids.length) return;
    await this.db.outbox.bulkDelete(ids);
  }

  async markOutboxFailure(id: string, error: string): Promise<void> {
    await this.db.outbox.where('id').equals(id).modify((entry) => {
      entry.attempts += 1;
      entry.lastError = error.slice(0, 500);
    });
  }

  async settleAcceptedOutbox(
    opId: string,
    entityKey: string,
    submittedBaseRevision: number,
    remoteRevision: number,
  ): Promise<void> {
    await this.db.transaction('rw', this.db.outbox, async () => {
      const current = await this.db.outbox.where('entityKey').equals(entityKey).first();
      if (!current) return;
      if (current.id === opId) {
        await this.db.outbox.delete(opId);
        return;
      }
      if (current.baseRevision === submittedBaseRevision) {
        await this.db.outbox.update(current.id, {
          baseRevision: remoteRevision,
          lastError: undefined,
        });
      }
    });
  }

  async settleResolvedOutbox(opId: string, entityKey: string): Promise<void> {
    await this.db.transaction('rw', this.db.outbox, async () => {
      const current = await this.db.outbox.where('entityKey').equals(entityKey).first();
      if (current?.id === opId) await this.db.outbox.delete(opId);
    });
  }

  async dump(): Promise<DatabaseDump> {
    const [records, attachments, settings, outbox] = await Promise.all([
      this.db.records.toArray(),
      this.db.attachments.toArray(),
      this.db.settings.toArray(),
      this.db.outbox.toArray(),
    ]);
    return { records, attachments, settings, outbox };
  }

  async restoreDump(dump: DatabaseDump, mode: RestoreMode): Promise<void> {
    await this.db.transaction(
      'rw',
      this.db.records,
      this.db.attachments,
      this.db.settings,
      this.db.outbox,
      async () => {
        if (mode === 'replace') {
          await Promise.all([
            this.db.records.clear(),
            this.db.attachments.clear(),
            this.db.settings.clear(),
            this.db.outbox.clear(),
          ]);
        }
        await this.db.records.bulkPut(dump.records);
        await this.db.attachments.bulkPut(dump.attachments);
        await this.db.settings.bulkPut(dump.settings);
        await this.db.outbox.bulkPut(dump.outbox);
      },
    );
  }

  async saveSnapshot(snapshot: SnapshotRecord): Promise<void> {
    await this.db.snapshots.put(snapshot);
  }

  async listSnapshots(): Promise<SnapshotRecord[]> {
    return this.db.snapshots.orderBy('createdAt').reverse().toArray();
  }

  async pruneSnapshots(keepDaily = 7, keepWeekly = 4): Promise<number> {
    const snapshots = await this.listSnapshots();
    const keep = new Set<string>();
    const daily = new Set<string>();
    const weekly = new Set<string>();
    for (const snapshot of snapshots) {
      const date = new Date(snapshot.createdAt);
      const dayKey = snapshot.createdAt.slice(0, 10);
      const firstDay = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
      const week = Math.ceil((((date.getTime() - firstDay.getTime()) / 86_400_000) + firstDay.getUTCDay() + 1) / 7);
      const weekKey = `${date.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
      if (daily.size < keepDaily && !daily.has(dayKey)) {
        daily.add(dayKey);
        keep.add(snapshot.id);
      }
      if (weekly.size < keepWeekly && !weekly.has(weekKey)) {
        weekly.add(weekKey);
        keep.add(snapshot.id);
      }
    }
    const remove = snapshots.filter((snapshot) => !keep.has(snapshot.id)).map((snapshot) => snapshot.id);
    await this.db.snapshots.bulkDelete(remove);
    return remove.length;
  }

  async close(): Promise<void> {
    this.db.close();
  }

  private makeOutbox(
    operation: OutboxEntry['operation'],
    entityKey: string,
    baseRevision: number,
    payload: unknown,
  ): OutboxEntry {
    return {
      id: createId('op'),
      operation,
      entityKey,
      baseRevision,
      payload,
      createdAt: isoNow(),
      attempts: 0,
    };
  }
}
